import React, { useState } from 'react';
import Select from 'react-select';
import firebase from 'firebase/compat/app';
import { db } from '../../../firebase';

export default function CampaignRequestLinkControl({ event, eventId = event?.id, requests, user, onLinked }) {
  const [pending, setPending] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const matchesUrl = !!eventId && event?.id === eventId;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const current = requests.find((option) => option.value.id === event?.capRequestId);
  const selected = pending ? requests.find((option) => option.value.id === pending.id) : current;
  const canLink = Number(user?.role) === 1 ||
    (Number(user?.role) <= 2 && /@core12\.com$/i.test(user?.email || ''));

  const link = async () => {
    if (!pending || !matchesUrl || !canLink || busy) return;
    setBusy(true);
    setError('');
    setMessage('');
    let linked = null;
    try {
      const result = await firebase.functions().httpsCallable('appV2/linkCampaignEvent')({
        id: pending.id, eventId, revision: pending.revision,
      });
      const snapshot = await db.collection('events').doc(eventId).get();
      linked = snapshot.exists ? { ...snapshot.data(), id: snapshot.id || eventId } : null;
      if (!snapshot.exists || linked?.capRequestId !== pending.id ||
          linked?.capRequestRevision !== pending.revision) {
        throw new Error('The link was submitted, but the event did not show the approved request. Reload and check it before trying again.');
      }
      setPending(null);
      setMessage(result.data?.message || 'Request linked to this event.');
    } catch (failure) {
      setError(failure?.message || 'Could not verify the link. Reload the event before trying again.');
    } finally {
      setBusy(false);
    }
    if (linked?.id === eventId && linked?.capRequestId === pending.id &&
        linked?.capRequestRevision === pending.revision) {
      try {
        await onLinked(linked);
      } catch {
        setError('The link was saved, but the editor could not refresh. Reload the event to see it.');
      }
    }
  };

  const disconnect = async () => {
    if (!matchesUrl || !canLink || !event?.capRequestId || busy) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await firebase.functions().httpsCallable('appV2/unlinkCampaignEvent')({
        id: event.capRequestId, eventId, revision: event.capRequestRevision ?? null,
      });
      const snapshot = await db.collection('events').doc(eventId).get();
      const next = snapshot.exists ? { ...snapshot.data(), id: snapshot.id || eventId } : null;
      if (!next || next.capRequestId || next.capSetup || next.allowPushToCap) {
        throw new Error('The disconnect was submitted, but could not be verified. Reload before trying again.');
      }
      setDisconnecting(false); setPending(null);
      setMessage(result.data?.message || 'Event disconnected.');
      try { await onLinked(next); }
      catch { setError('The event was disconnected, but the editor could not refresh. Reload the event.'); }
    } catch (failure) {
      setError(failure?.message || 'Could not disconnect this event.');
    } finally { setBusy(false); }
  };

  return <div className="mb-4 space-y-2">
    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300"
      htmlFor="approved-campaign-request">Approved campaign request</label>
    <Select inputId="approved-campaign-request" classNamePrefix="manage-select"
      options={requests} value={selected || null}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.value?.id}
      isDisabled={!matchesUrl || !canLink || !!event?.capRequestId || busy}
      onChange={(item) => { setPending(item?.value || null); setError(''); setMessage(''); }} />
    {event?.capRequestId && current && current.value.revision !== event.capRequestRevision &&
      <button type="button" className="text-sm underline" disabled={!matchesUrl || !canLink || busy || disconnecting}
        onClick={() => { setPending(current?.value || null); setError(''); setMessage(''); }}>
        Apply latest approved revision
      </button>}
    {event?.capRequestId && <p className="text-sm text-zinc-700 dark:text-zinc-300">
      Linked to request {event.capRequestId}, revision {event.capRequestRevision}.
      {' '}<a className="underline" href={`/manage/campaign-code-requests/${event.capRequestId}`}>
        Open approval journey to confirm setup</a>
    </p>}
    {!event?.capRequestId && <p className="text-sm text-zinc-700 dark:text-zinc-300">
      Choose a request, then use the link button below. Linking saves the approved C@P
      settings to this event. After linking, export needs setup confirmation. Save any other event edits first.
    </p>}
    {!matchesUrl && <p role="alert" className="text-sm text-red-700 dark:text-red-300">
      Reload this event. The editor does not match the current event URL.
    </p>}
    {canLink && event?.capRequestId && !disconnecting && <button type="button"
      className="text-sm underline" disabled={!matchesUrl || busy}
      onClick={() => { setDisconnecting(true); setPending(null); setError(''); }}>
      Disconnect campaign request
    </button>}
    {disconnecting && <div className="rounded-md border border-zinc-300 p-3 dark:border-zinc-700">
      <p className="text-sm font-semibold">Disconnect {event?.name || eventId} from request {event.capRequestId}?</p>
      <p className="mt-1 text-sm">C@P exports will pause until an approved request is linked and setup is confirmed. Event details and campaign codes are retained for review.</p>
      <div className="mt-3 flex gap-2">
        <button type="button" className="rounded-md border px-4 py-2 text-sm disabled:opacity-40"
          disabled={!matchesUrl || !canLink || busy} onClick={disconnect}>
          {busy ? 'Disconnecting…' : 'Confirm disconnect'}
        </button>
        <button type="button" className="text-sm underline" disabled={busy}
          onClick={() => { setDisconnecting(false); }}>Cancel</button>
      </div>
    </div>}
    {pending && <div className="rounded-md border border-zinc-300 p-3 dark:border-zinc-700">
      <p className="text-sm font-semibold">Ready to link: {pending.eventName} to {event?.name || eventId}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className="rounded-md bg-zinc-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40 dark:bg-white dark:text-zinc-900"
          disabled={!matchesUrl || !canLink || busy} onClick={link}>
          {busy ? 'Linking…' : 'Link request to this event'}
        </button>
        <button type="button" className="rounded-md border border-zinc-300 px-4 py-2 text-sm dark:border-zinc-700"
          disabled={busy} onClick={() => { setPending(null); setError(''); }}>
          Cancel
        </button>
      </div>
    </div>}
    {error && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{error}</p>}
    {message && <p role="status" className="text-sm text-green-700 dark:text-green-300">{message}</p>}
  </div>;
}
